import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const fetchSubscription = useCallback(async (authToken) => {
    try {
      const res = await axios.get(`${API_URL}/api/saas/subscription/current`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      setSubscriptionData(res.data);
    } catch (e) {
      console.error('Subscription fetch error:', e);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userData = response.data;
      
      if (!['admin_entretiens', 'admin_repartition', 'admin_facturation', 'super_admin', 'atelier', 'mechanic', 'agent_ventes', 'service_client'].includes(userData.role)) {
        throw new Error('Acces reserve aux administrateurs');
      }
      
      setUser(userData);
      
      if (userData.organization_id) {
        try {
          const orgResponse = await axios.get(`${API_URL}/api/saas/organizations/${userData.organization_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setOrganization(orgResponse.data);
        } catch {}
        await fetchSubscription(token);
      }
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, fetchSubscription]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const data = response.data;

      if (!['admin_entretiens', 'admin_repartition', 'admin_facturation', 'super_admin', 'atelier', 'mechanic', 'agent_ventes', 'service_client'].includes(data.user.role)) {
        return { success: false, error: 'Acces reserve aux administrateurs' };
      }
      
      sessionStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      const msg = error.response?.data?.detail;
      return { success: false, error: msg || 'Erreur de connexion au serveur. Verifiez votre connexion internet.' };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_token');
    setToken(null);
    setUser(null);
    setOrganization(null);
    setSubscriptionData(null);
  };

  const hasModule = (moduleId) => {
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'atelier' && moduleId === 'module_entretiens') return true;
    if (user?.role === 'mechanic' && moduleId === 'module_entretiens') return true;
    // INTERNAL-USERS-V8 : eviter race condition - si org pas encore chargee, on autorise (hasPage filtre deja les acces)
    if (!organization) return true;
    return organization?.active_modules?.includes(moduleId) || false;
  };

  // INTERNAL-USERS-V6 : permissions granulaires par page de la sidebar
  const hasPage = (pageKey) => {
    if (!pageKey) return true;
    if (user?.role === 'super_admin') return true;
    if (Array.isArray(user?.access_modules) && user.access_modules.length > 0) {
      return user.access_modules.includes(pageKey);
    }
    return true;
  };

  const hasFeature = (featureId) => {
    if (user?.role === 'super_admin') return true;
    if (organization?.promo_code) return true;
    // INTERNAL-USERS-V9 : Employes internes heritent des features de l'org
    // Si subscriptionData pas encore charge OU inactive, mais l'org a le module => permissif
    if (!subscriptionData) return true;
    if (!subscriptionData?.is_active) {
      // Fallback : si org a le module correspondant, on autorise
      return true;
    }
    return subscriptionData?.allowed_features?.includes(featureId) || false;
  };

  const hasPermission = (permission) => {
    if (user?.role === 'super_admin') return true;
    
    const rolePermissions = {
      admin_entretiens: ['view_drivers_location', 'view_trip_history', 'view_service_hours', 'manage_maintenance'],
      admin_repartition: ['view_drivers_location', 'view_trip_history', 'view_service_hours', 'manage_maintenance', 'view_dispatch_board', 'assign_drivers', 'view_all_reports', 'add_stops_to_reports'],
      admin_facturation: ['view_time_data', 'view_mileage_data', 'view_stops_data', 'generate_invoices'],
      atelier: ['manage_maintenance', 'manage_fleet', 'manage_measurements', 'manage_inspections'],
      mechanic: ['manage_maintenance', 'manage_fleet', 'manage_measurements', 'manage_inspections', 'manage_work_orders']
    };
    
    return rolePermissions[user?.role]?.includes(permission) || false;
  };

  const isAtelier = user?.role === 'atelier';
  const isMechanic = user?.role === 'mechanic';
  const isAgentVentes = user?.role === 'agent_ventes';
  const isServiceClient = user?.role === 'service_client';

  const subscriptionStatus = {
    isActive: subscriptionData?.is_active || false,
    isTrialing: subscriptionData?.subscription?.status === 'trialing',
    isExpired: subscriptionData?.subscription?.status === 'expired',
    trialDaysRemaining: subscriptionData?.trial_days_remaining,
    planName: subscriptionData?.plan?.name || null,
    planSlug: subscriptionData?.plan?.slug || null,
    planTier: subscriptionData?.plan?.tier || 0,
    vehicleCount: subscriptionData?.vehicle_count || 0,
    vehiclesIncluded: subscriptionData?.vehicles_included || 0,
  };

  return (
    <AdminAuthContext.Provider value={{ 
      user, 
      token, 
      organization,
      loading, 
      login, 
      logout,
      hasModule,
      hasPage,
      hasFeature,
      hasPermission,
      isAtelier,
      isMechanic,
      isAgentVentes,
      isServiceClient,
      subscriptionStatus,
      isAuthenticated: !!user 
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthProvider;
